import React from "react";
import PropTypes from "prop-types";
import MultiStyleButton from "../../../../systemUtils/MultiStyleButton";

const UserStep4ActionsApprovalGate = ({
  setCardStep,
  companyStructure,
  userSystem,
  onChange,
  finishFlow,
}) => {
  // For clarity, store everything in userSystem.actionsApprovalGate
  const actionsGate = userSystem.actionsApprovalGate || {};
  const isApprovalRequired = actionsGate.isRequired || false;
  const isMultipleApprovals = actionsGate.isMultipleApprovals || false;

  const selectedApproverGroups = actionsGate.approverGroups || [];
  const selectedApproverRoles = actionsGate.approverRoles || [];

  // #1 Toggle top-level "Require Approval" for Revoke/Restore/Delete
  const handleApprovalRequiredCheckbox = (e) => {
    const newIsRequired = e.target.checked;

    if (!newIsRequired) {
      // turn off => clear
      onChange({
        ...userSystem,
        actionsApprovalGate: {
          isRequired: false,
          isMultipleApprovals: false,
          approverGroups: [],
          approverRoles: [],
        },
      });
      return;
    }

    // turn on => keep or init
    onChange({
      ...userSystem,
      actionsApprovalGate: {
        ...actionsGate,
        isRequired: true,
        isMultipleApprovals: actionsGate.isMultipleApprovals || false,
      },
    });
  };

  // #2 "Need multiple approvals?" => toggles group-based vs. role-based
  const handleMultipleApprovalsCheckbox = (e) => {
    const newVal = e.target.checked;
    if (newVal) {
      // turn on => clear group approach
      onChange({
        ...userSystem,
        actionsApprovalGate: {
          ...actionsGate,
          isMultipleApprovals: true,
          approverGroups: [],
          approverRoles: actionsGate.approverRoles || [],
        },
      });
    } else {
      // turn off => revert to group-based
      onChange({
        ...userSystem,
        actionsApprovalGate: {
          ...actionsGate,
          isMultipleApprovals: false,
          approverRoles: [],
          approverGroups: actionsGate.approverGroups || [],
        },
      });
    }
  };

  // #3 Single approvals => group checkboxes
  const handleCheckboxForGroup = (groupName) => {
    const current = selectedApproverGroups;
    let newArr;
    if (current.includes(groupName)) {
      newArr = current.filter((g) => g !== groupName);
    } else {
      newArr = [...current, groupName];
    }

    onChange({
      ...userSystem,
      actionsApprovalGate: {
        ...actionsGate,
        isMultipleApprovals: false,
        approverGroups: newArr,
      },
    });
  };

  // #4 Flatten roles => multiple approvals
  const allRoles = companyStructure?.userGroups
    ? companyStructure.userGroups.flatMap((g) =>
        g.roles.map((r) => ({
          groupName: g.groupName,
          roleName: r.roleName,
        }))
      )
    : [];

  const getRoleKey = (groupName, roleName) => `${groupName}||${roleName}`;

  const handleCheckboxForRole = (roleKey) => {
    let newArr;
    if (selectedApproverRoles.includes(roleKey)) {
      newArr = selectedApproverRoles.filter((r) => r !== roleKey);
    } else {
      newArr = [...selectedApproverRoles, roleKey];
    }

    onChange({
      ...userSystem,
      actionsApprovalGate: {
        ...actionsGate,
        isMultipleApprovals: true,
        approverRoles: newArr,
      },
    });
  };

  // #5 Validation before finishing
  const handleFinish = () => {
    if (!isApprovalRequired) {
      // no approvals needed
      finishFlow();
      return;
    }

    if (!isMultipleApprovals) {
      // group-based
      if (selectedApproverGroups.length === 0) {
        alert(
          "At least one user group must be selected if approval is required for Revoke/Restore/Delete."
        );
        return;
      }
    } else {
      // role-based
      if (selectedApproverRoles.length === 0) {
        alert(
          "At least one user role must be selected if multiple approvals are required for Revoke/Restore/Delete."
        );
        return;
      }
    }

    finishFlow();
  };

  return (
    <div>
      <h2 className="card-title">Approval for Revoke, Restore, and Delete</h2>
      <p className="card-subtitle">
        By default, no extra approval is required to revoke, restore, or delete
        users; the same permissions you set for adding users automatically apply
        here. In other words, anyone who can add a user at a certain level can
        also perform these actions at that level. If you want to introduce a
        separate approval process for these actions, check the box below. You
        can also enable multiple approvals, which means everyone in the selected
        roles must approve before the action is finalized.
      </p>

      {/* 1) Top-level "Require Approval" */}
      <div className="role-toggle-row">
        <label>
          <input
            type="checkbox"
            checked={isApprovalRequired}
            onChange={handleApprovalRequiredCheckbox}
          />
          Require Approval before revoking, restoring, or deleting a user?
        </label>
      </div>

      {isApprovalRequired && (
        <div className="approval-frame">
          <div className="multiple-approvals-container">
            <label className="multiple-approvals-check">
              <input
                type="checkbox"
                checked={isMultipleApprovals}
                onChange={handleMultipleApprovalsCheckbox}
              />
              Need multiple approvals?
            </label>
            <p className="multiple-approvals-explainer">
              Sometimes you need extra security. With multiple approvals,
              <strong> all selected roles</strong> must grant approval before
              these actions are taken.
            </p>
          </div>

          {!isMultipleApprovals ? (
            <>
              <p className="approval-title">
                Which user groups will have approval rights?
              </p>
              {companyStructure?.userGroups?.map((g) => (
                <label key={g.groupName} className="approval-group-label">
                  <input
                    type="checkbox"
                    checked={selectedApproverGroups.includes(g.groupName)}
                    onChange={() => handleCheckboxForGroup(g.groupName)}
                  />
                  {g.groupName}
                </label>
              ))}
            </>
          ) : (
            <>
              <p className="approval-title">
                Which user roles will have approval rights?
              </p>
              {allRoles.map((roleObj) => {
                const roleKey = getRoleKey(roleObj.groupName, roleObj.roleName);
                const isChecked = selectedApproverRoles.includes(roleKey);
                return (
                  <label key={roleKey} className="approval-group-label">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleCheckboxForRole(roleKey)}
                    />
                    {roleObj.groupName} - {roleObj.roleName}
                  </label>
                );
              })}
            </>
          )}
        </div>
      )}

      <div className="continue-button-container">
        <MultiStyleButton
          label="Back"
          buttonType="cancel"
          onClick={() => setCardStep(3)}
        />
        <MultiStyleButton
          label="Finish"
          buttonType="approve"
          onClick={handleFinish}
        />
      </div>
    </div>
  );
};

UserStep4ActionsApprovalGate.propTypes = {
  setCardStep: PropTypes.func.isRequired,
  companyStructure: PropTypes.object.isRequired,
  userSystem: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  finishFlow: PropTypes.func.isRequired,
};

export default UserStep4ActionsApprovalGate;
